import React, {FC, useMemo} from "react";
import {
	Box,
	Typography
} from "@mui/material";
import Check from "@mui/icons-material/Check";
import {makeStyles} from "tss-react/mui";
import {isNotOverdue, ModuleDuration} from "@/app2/pages/HomePage/TodayLearningSection/TodayLearningCard";
import {palette} from "@/constants";
import {useSSTranslation} from "@/i18n/useSSTranslation";

type BorderVariant = "orange" | "error" | "neutral";

interface IMenuLessonCardProps {
	title: string;
	duration: number;
	dueDate?: Date | null;
	imageUrl: string;
	courseEnrollmentType?: string;
	completed: boolean;
	isActive?: boolean;
}

const useStyles = makeStyles<{ borderVariant: BorderVariant }>()((theme, {borderVariant}) => ({
	card: {
		position: "relative",
		display: "flex",
		padding: "14px",
		alignItems: "center",
		gap: "10px",
		borderRadius: "16px",
		borderWidth: borderVariant === "orange" ? "2px" : "1px",
		borderStyle: "solid",
		borderColor:
			borderVariant === "orange"
				? palette.orange
				: borderVariant === "error"
					? theme.palette.error.main
					: "rgba(161, 161, 161, 0.15)",
		background: "#363636",
		cursor: "pointer",
		transition: "box-shadow 0.2s ease, transform 0.2s ease",
		"&:hover": {
			boxShadow: theme.shadows[3],
			transform: "translateY(-2px)"
		}
	},
	completedBadge: {
		position: "absolute",
		top: 0,
		right: 0,
		transform: "translate(50%, -50%)",
		width: 22,
		height: 22,
		borderRadius: "50%",
		background: palette.orange,
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		zIndex: 2,
		boxSizing: "border-box",
		"& svg": {
			width: 14,
			height: 14,
			color: theme.palette.common.black
		}
	},
	imageContainer: {
		position: "relative",
		width: "150px",
		height: "112px",
		borderRadius: "12px",
		overflow: "hidden",
		flexShrink: 0
	},
	image: {
		width: "100%",
		height: "100%",
		objectFit: "cover"
	},
	content: {
		display: "flex",
		flexDirection: "column",
		gap: 4,
		flex: 1,
		minWidth: 0
	},
	title: {
		fontWeight: 600,
		display: "-webkit-box",
		WebkitLineClamp: 2,
		WebkitBoxOrient: "vertical",
		overflow: "hidden"
	},
	footer: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		marginTop: 4
	},
	adminText: {
		fontSize: 12,
		color: theme.palette.text.secondary
	},
	dateChip: {
		height: 22,
		fontSize: 12
	}
}));

export const MenuLessonCard: FC<IMenuLessonCardProps> = ({
	title,
	completed,
	duration,
	dueDate,
	courseEnrollmentType,
	imageUrl,
	isActive = false,
	...props
}) => {
	const borderVariant = useMemo((): BorderVariant => {
		if (isActive || completed) {
			return "orange";
		}
		if (!isNotOverdue(dueDate ?? null)) {
			return "error";
		}
		return "neutral";
	}, [isActive, completed, dueDate]);

	const {classes} = useStyles({borderVariant});
	const {t} = useSSTranslation();

	return (
		<Box
			className={classes.card}
			role="button"
			tabIndex={0}
			aria-label={`Lesson card: ${title}${completed ? ", completed" : ""}${isActive ? ", current" : ""}`}
			data-test-id="menu-lesson-card"
			{...props}
		>
			{completed && (
				<Box className={classes.completedBadge} aria-hidden data-test-id="menu-lesson-card-completed-badge">
					<Check />
				</Box>
			)}
			<Box
				className={classes.imageContainer}
				aria-label="Lesson preview image"
				data-test-id="menu-lesson-card-image-container"
			>
				<img
					src={imageUrl}
					alt=""
					className={classes.image}
					data-test-id="menu-lesson-card-image"
				/>
			</Box>

			<Box
				className={classes.content}
				data-test-id="menu-lesson-card-content"
			>
				<ModuleDuration durationInSeconds={duration} complete={completed} dueDate={dueDate ?? null} />
				<Typography
					variant="body2"
					className={classes.title}
					data-test-id="menu-lesson-card-title"
				>
					{title}
				</Typography>

				{(courseEnrollmentType) && (
					<Typography
						variant="body2"
						component="div"
						align="left"
						color={palette.grey70}
					>
						{courseEnrollmentType && t(`user_enrollment_type_${courseEnrollmentType}` as any, courseEnrollmentType)}
					</Typography>
				)}
			</Box>
		</Box>
	);
};
